import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

import { Validator } from 'src/app/shared/validators/validators';
import { SmsMessage, SmsProvider, SmsRelayType } from '../communication.constants';
import { MessagesService } from '../services/MessagesService/messages.service';
import { ENTER, SPACE } from '@angular/cdk/keycodes';
import { MatChipInputEvent } from '@angular/material/chips';
import { ConfigurationsService } from 'src/app/configurations/services/configurations.service';
import { Variable, VariableMap } from 'src/app/shared/types/common';

const NATIVE_SMS_PROVIDER: SmsProvider = {
  id: 'device',
  name: 'Dispositivo actual',
  description: 'Enviar SMS utilizando o dispositivo em uso',
  relayType: SmsRelayType.DEVICE,
  enabled: true,
};

export type Contact = {
  msisdn: string;
  data?: {};
};

export type SmsSendPopupComponentData = {
  contacts: Contact[];
  variables?: VariableMap;
};

@Component({
  selector: 'cm-sms-send-popup',
  templateUrl: './sms-send-popup.component.html',
  styleUrls: ['./sms-send-popup.component.scss'],
})
export class SmsSendPopupComponent implements OnInit {
  SmsRelayType = SmsRelayType;

  readonly separatorKeysCodes = [SPACE, ENTER] as const;

  smsWords = [];

  formAttributes = {
    text: 'text',
    labels: 'labels',
    provider: 'provider',
    date: 'date',
  };

  formGroup = new UntypedFormGroup({
    [this.formAttributes.text]: new UntypedFormControl(null, Validators.minLength(1)),
    [this.formAttributes.provider]: new UntypedFormControl(null, Validators.required),
    [this.formAttributes.date]: new UntypedFormControl(null),
  });

  smsProviders: SmsProvider[] = [];
  smsVariables: VariableMap = {};
  smsVariablesFlat: Variable[] = [];

  count = 0;
  isLoading = false;

  sampleIndex = 0;
  sampleMessage = '';
  sampleMessageLength = 0;

  domParser = new DOMParser();

  get selectedRelayType() {
    return this.formGroup.get(this.formAttributes.provider).value?.relayType;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: SmsSendPopupComponentData,
    private matDialogRef: MatDialogRef<SmsSendPopupComponent>,
    private snackBar: MatSnackBar,
    private communicationService: MessagesService,
    private configurationsService: ConfigurationsService
  ) {
    this.count = this.data.contacts.filter((contact) => Validator.Msisdn(contact.msisdn)).length;
    this.communicationService.getSmsProviders().subscribe((providers) => {
      this.smsProviders = [...providers, NATIVE_SMS_PROVIDER];
    });

    this.smsVariables = this.data.variables;
    this.smsVariablesFlat = Object.keys(this.smsVariables).reduce((acc, key) => [...acc, ...this.smsVariables[key]], []);
  }

  ngOnInit(): void {}

  isVariable(word: any) {
    return (
      word.key &&
      Object.keys(this.data.variables).find((key) => this.data.variables[key].find((variable) => `{${key}.${variable.name}}` === word.key))
    );
  }

  addVariable(variable: Variable) {
    this.smsWords.push(variable);
  }

  edit() {
    console.log('EDIT!');
  }

  parseSampleMessage() {
    const content = this.formGroup.get(this.formAttributes.text).value || '';
    const contact = this.data.contacts[this.sampleIndex];

    this.configurationsService.getVariableContentResult(content, contact.data, this.smsVariables).subscribe((result) => {
      this.sampleMessage = result;
      this.sampleMessageLength = result.length;
    });
  }

  decreaseSampleIndex() {
    this.sampleIndex--;
    this.parseSampleMessage();
  }

  increaseSampleIndex() {
    this.sampleIndex++;
    this.parseSampleMessage();
  }

  smsTextChanged(content: string) {
    const document = this.domParser.parseFromString(content, 'text/html');
    this.formGroup.get(this.formAttributes.text).setValue(document.body.textContent);
    this.parseSampleMessage();
  }

  add(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();

    if (value) {
      this.smsWords.push({ text: value });
    }

    event.chipInput!.clear();
  }

  remove(word: string): void {
    const index = this.smsWords.indexOf(word);

    if (index >= 0) {
      this.smsWords.splice(index, 1);
    }
  }

  async send() {
    this.isLoading = true;

    const provider = this.formGroup.get('provider').value as SmsProvider;
    const date = new Date(this.formGroup.get('date').value);
    const content = this.formGroup.get(this.formAttributes.text).value || '';

    if (provider.relayType === SmsRelayType.DEVICE) {
      this.data.contacts
        .filter((contact) => contact.msisdn?.trim().length)
        .forEach(async (contact) => {
          const parsedMessage = await this.configurationsService.getVariableContentResult(content, contact.data, this.smsVariables).toPromise();

          this.communicationService.sendDeviceSms(contact.msisdn, parsedMessage);
        });

      this.isLoading = false;

      return;
    }

    const messages = await Promise.all(
      this.data.contacts
        .filter((contact) => contact.msisdn?.trim().length)
        .map(async (contact) => {
          const parsedMessage = await this.configurationsService.getVariableContentResult(content, contact.data, this.smsVariables).toPromise();

          return {
            to: contact.msisdn,
            message: parsedMessage,
          } as SmsMessage;
        })
    );

    this.communicationService.scheduleSms(messages, provider, date).subscribe(
      () => {
        this.snackBar.open(`SMS queued for sending to ${this.data.contacts.length} destinations!`, 'Close', {
          panelClass: 'mat-mdc-snack-bar-container--success',
          verticalPosition: 'top',
        });

        this.matDialogRef.close();
      },
      (error) => {
        this.snackBar.open(`Error sendind SMS: ${error}`, 'Close', {
          panelClass: 'mat-mdc-snack-bar-container--error',
          verticalPosition: 'top',
        });
      },
      () => {
        this.isLoading = false;
      }
    );
  }

  cancel() {
    this.matDialogRef.close();
  }

  ignoreEvent(event: MouseEvent) {
    event.stopImmediatePropagation();
    event.preventDefault();
  }
}
